import { useMemo } from 'react';
import { useDrop } from 'react-dnd';

import { capitalize } from '../../utils';
import dropIcon from '../../assets/drop-icon.svg';
import {
  myDataZoneType,
  canAddAttribute,
  canAddMyDataResource,
} from '../../helpers/dataDictionary';
import { getZoneVariables } from '../../helpers/expressionTree';
import ExpressionTreeRenderer from './ExpressionTreeRenderer';
import SelectableStaticList from './SelectableStaticList';
import DeleteCheckbox from '../inputs/DeleteCheckbox';
import {
  TYPES,
  ADD_ATTRIBUTE,
  REMOVE_MY_DATA_RESOURCE,
  ADD_MY_DATA_RESOURCE,
} from '../../reducers/builderReducer';

const { VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE } = TYPES;

function DropIconCircle({ className }) {
  return (
    <div
      className={`bg-background-dark w-16 h-16 rounded-full p-3.5 ${
        className ? className : ''
      }`}
    >
      <img className="w-full h-full" src={dropIcon} alt="drop here icon" />
    </div>
  );
}

function ResourcesList({ resourcesMap, builderDispatch, zoneType }) {
  return (
    <ul>
      {Object.entries(resourcesMap).map(([key, value], idx) => {
        const items = value.map(({ id, name, type, state: initialState }) => (
          <div className="flex flex-row items-center py-1" key={id}>
            <DeleteCheckbox
              className="mr-2"
              onClick={() =>
                builderDispatch({
                  type: REMOVE_MY_DATA_RESOURCE,
                  payload: {
                    id,
                    name,
                    type,
                    zoneType,
                  },
                })
              }
            />
            {name}
          </div>
        ));
        return items?.length > 0 ? (
          <li key={idx}>
            <SelectableStaticList
              label={key}
              items={items}
              initialOpen={true}
            />
          </li>
        ) : null;
      })}
    </ul>
  );
}

// PR 2b-5: `isOrQueryContainer` and `VariablesList` were removed — the
// recursive ExpressionTreeRenderer (PR 2b-2) replaced VariablesList, and
// the per-key-OR visual cue is now handled natively by the renderer.

// ===========================================================================
// Outer drop handler (PR 2b-4 regression-guarded export)
// ===========================================================================
// When a user drops a dictionary item ON TOP OF an existing card, three
// drop targets fire: the card, the surrounding group (if any), and this
// outer dropzone. The card handles the drop with ADD_ATTRIBUTE_AND_WRAP
// (or WRAP_IN_GROUP for tree-internal drags); the group skips via its own
// `monitor.didDrop()` guard. Without the guard below, the outer dropzone
// would ALSO fire ADD_ATTRIBUTE — creating a duplicate at the root.
//
// Exported for regression testing: see VariablesDropzone.test.ts.

export function handleOuterDrop(
  item: any,
  monitor: { didDrop: () => boolean; getItemType: () => string | symbol | null },
  zoneType: string,
  builderDispatch: (action: any) => void,
) {
  if (monitor.didDrop()) return;
  const type = monitor.getItemType();

  if (type === VARIABLE_ATTRIBUTE) {
    builderDispatch({
      type: ADD_ATTRIBUTE,
      payload: {
        id: item.id,
        name: item.name,
        zoneType,
      },
    });
    return;
  }

  if (type === MY_DATA_ATTRIBUTE) {
    builderDispatch({
      type: ADD_MY_DATA_RESOURCE,
      payload: {
        id: item.id,
        name: item.name,
        type: item.resourceType,
        prefix: item.prefix,
        zoneType,
      },
    });
  }
}

type VariablesDropzoneProps = {
  className?: string;
  dropzoneMessage: string;
  zoneType: string;
  builderState: any;
  builderDispatch: any;
};

export default function VariablesDropzone({
  className,
  dropzoneMessage,
  zoneType,
  builderState,
  builderDispatch,
}: VariablesDropzoneProps) {
  // Flatten the zone's expression tree to a VariableState[]. PR 2b-1: tree
  // is always a root AND of leaves so this is equivalent to the old
  // `state.include` / `state.exclude` array.
  const variables = useMemo(
    () => getZoneVariables(builderState, zoneType),
    [builderState, zoneType],
  );

  const [{ isOver, canDrop, itemId }, drop] = useDrop(
    () => ({
      accept: [VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE],
      drop: (item: any, monitor: any) =>
        // Extracted to a pure function so the didDrop guard is regression-
        // testable without mounting react-dnd's full machinery.
        handleOuterDrop(item, monitor, zoneType, builderDispatch),
      canDrop: (item, monitor) => {
        const type = monitor.getItemType();

        if (type === VARIABLE_ATTRIBUTE) {
          if (!item.allowedZoneTypes?.includes(zoneType)) {
            return false;
          }
          const otherZone = zoneType === 'include' ? 'exclude' : 'include';
          const otherZoneVariables = getZoneVariables(builderState, otherZone);
          const canAdd =
            canAddAttribute(variables, item.id) &&
            canAddAttribute(otherZoneVariables, item.id);
          return canAdd;
        }

        // PR 2c: audiences/files always allowed; the reducer handles
        // duplication semantics. (Same-id duplicates would be visible to
        // the user via two cards, mirroring the legacy myData behavior.)
        if (type === MY_DATA_ATTRIBUTE) return true;

        return false;
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
        itemId: monitor.getItem()?.id,
      }),
    }),
    [builderState]
  );

  const map = useMemo(() => {
    let map = {};

    variables.forEach(({ id, state }) => {
      const idPath = id.split('___');

      let curSelector;
      idPath.forEach((selector, idx) => {
        if (idx === idPath.length - 1) {
          curSelector.attributes.push({
            name: selector,
            id,
            state,
          });
          return;
        }

        if (!curSelector) {
          if (!map[selector]) {
            map[selector] = {
              attributes: [],
            };
          }
          curSelector = map[selector];
          return;
        }

        if (!curSelector[selector]) {
          curSelector[selector] = {
            attributes: [],
          };
        }

        curSelector = curSelector[selector];
      });
    });

    return map;
  }, [variables]);

  // PR 2c: myData (audiences/files) live as leaves in the same expression
  // tree as variables, so a single "is the zone non-empty?" check covers
  // both. We test the underlying tree because `getZoneVariables` filters
  // to variable leaves and would miss a zone holding only audience/file
  // membership leaves.
  const hasContent = useMemo(() => {
    const tree =
      zoneType === 'include'
        ? builderState.expression
        : builderState.excludeExpression;
    return tree !== null;
  }, [builderState.expression, builderState.excludeExpression, zoneType]);

  // PR 2b-5: single-column layout. The previous LEFT-side VariableCategory
  // summaries are gone — each card has its own top-right "×" close button
  // now, so the redundant summary list isn't needed. MyData resources
  // (uploaded files / purchased audiences) render compactly at the top of
  // the cards area; variables render below; an empty drop strip sits at
  // the bottom for adding to the root. Drops anywhere in the zone still
  // work — the outer useDrop is wired to the whole container.

  return (
    <div
      ref={drop}
      data-tour="variables-dropzone"
      data-tour-zone={zoneType}
      className={[
        'flex flex-col flex-grow bg-gray-100 rounded-half',
        hasContent ? 'p-4 space-y-3' : '',
        className ?? '',
      ].join(' ')}
    >
      {hasContent ? (
        <>
          <ExpressionTreeRenderer
            tree={
              zoneType === 'include'
                ? builderState.expression
                : builderState.excludeExpression
            }
            zoneType={zoneType}
            builderState={builderState}
            builderDispatch={builderDispatch}
          />
          {/* Bottom drop strip — accepts root-level adds when there are
              already cards in the zone. The outer container is the actual
              react-dnd target; this strip is just visual affordance. */}
          <div
            className={[
              'flex flex-row items-center justify-center rounded-half border-2 border-dashed py-3 transition-colors',
              isOver && canDrop ? 'border-teal-500 bg-teal-50' : 'border-gray-300',
            ].join(' ')}
          >
            <p className="text-gray-500 text-xs">
              {isOver && !canDrop
                ? itemId?.toLowerCase()?.includes('geography')
                  ? 'Only one geography variable is allowed at once.'
                  : 'Attribute has already been pulled over.'
                : 'Drag and drop more variables here'}
            </p>
          </div>
        </>
      ) : (
        // Empty state — full-zone drop area with the welcome message and icon.
        <div
          className={[
            'flex flex-col justify-center items-center rounded-half p-3 py-5 border-2 border-dashed flex-grow transition-colors',
            isOver && canDrop ? 'border-teal-500 bg-teal-50' : 'border-gray-300',
            isOver && !canDrop ? 'cursor-no-drop bg-gray-200' : '',
          ].join(' ')}
        >
          <div className="flex flex-col items-center">
            <DropIconCircle
              className={`${
                isOver ? (canDrop ? 'bg-primary' : 'bg-secondary') : ''
              }`}
            />
            <p className="pt-6 text-gray-500 text-center">
              {isOver && !canDrop
                ? itemId?.toLowerCase()?.includes('geography')
                  ? 'Only one geography variable is allowed at once.'
                  : 'Attribute has already been pulled over.'
                : dropzoneMessage}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
